import { ChangeDetectorRef, Component, ElementRef, Input, OnDestroy, OnInit, ChangeDetectionStrategy, inject, input, viewChild } from "@angular/core";
import { FormControl, FormsModule, ReactiveFormsModule } from "@angular/forms";
import * as _ from "lodash-es";
import { BehaviorSubject, Observable, Subscription } from "rxjs";
import { AlertService } from "src/app/services/alert.service";
import { FileService, IUploadedFile } from "src/app/services/file.service";
import { VIEWMODE } from "../../pages/view/view.component";
import { ProjectService } from "src/app/services/project.service";
import { AuthService } from "src/app/services/auth.service";
import JSZip from "jszip";
import { NgClass, AsyncPipe } from "@angular/common";
import { MatIcon } from "@angular/material/icon";
import { MatProgressSpinner } from "@angular/material/progress-spinner";
import { RxReactiveFormsModule } from "@rxweb/reactive-form-validators";
import { MatButton } from "@angular/material/button";
import { TranslatePipe } from "@ngx-translate/core";

enum FileUploaderMode {
    UPLOAD = 'upload',
    DOWNLOADS = 'download',
}

@Component({
    selector: "app-file-uploader",
    templateUrl: "./file-uploader.component.html",
    styleUrls: ["./file-uploader.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [NgClass, MatIcon, MatProgressSpinner, RxReactiveFormsModule, FormsModule, ReactiveFormsModule, MatButton, AsyncPipe, TranslatePipe]
})
export class FileUploaderComponent implements OnInit, OnDestroy {
    protected alert = inject(AlertService);
    protected s_files = inject(FileService);
    protected s_project = inject(ProjectService);
    protected s_auth = inject(AuthService);
    protected cd = inject(ChangeDetectorRef);

    private fileTypes: { [k: string]: string | string[]; } = {
        pdf: "application/pdf",
        jpg: "image/jpeg",
        jpeg: "image/jpeg",
        png: "image/png",
        ppt: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
        pptx: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
        xls: "application/vnd.ms-excel",
        xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        doc: "application/msword",
        docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        // ai: "application/postscript",
        ai: "application/illustrator",
        psb: "image/vnd.adobe.photoshop",
        // eps: "application/postscript",
        eps:"image/x-eps",
        csv: "text/csv",
        dxf: [
            "image/vnd.dxf",
            "application/dxf",
            "application/x-autocad",
            "application/x-dxf",
            "image/x-autocad",
            "image/x-dxf",
            "zz-application/zz-winassoc-dxf"
        ]
    };

    isModal: boolean = false;

    readonly upBlock = viewChild<ElementRef>("upBlock");

    readonly view_mode = input<VIEWMODE>();
    readonly maxFileSize = input<number>(20);
    readonly fileFormats = input<string[]>([]);
    @Input() multiple: boolean | number = false;
    readonly control = input<FormControl>();
    readonly clearAndOpenModal = input<boolean>(false);

    archiving: boolean = false;
    existing_errors: any = {};

    get hasFormatWildcard() {
        const fileFormats = this.fileFormats();
        return fileFormats && fileFormats.includes('*');
    }

    get isMultipleANumber() {
        return _.isNumber(this.multiple);
    }

    get FileUploaderMode() {
        return FileUploaderMode;
    }

    get readOnly() {
        return this.view_mode() === VIEWMODE.VIEW;
    }

    get isClient() {
        return this.s_auth.isClient();
    }

    public mode: FileUploaderMode = FileUploaderMode.UPLOAD;
    public uploaderId: string = `file-uploader-input-${this.generateUniqueIdentifier(10)}`;
    public fileControl: FormControl = new FormControl();

    public get current(): IUploadedFile[] | File[] | null {
        return this.control()?.value as (IUploadedFile[] | File[] | null);
    }
    set current(value: IUploadedFile[] | File[] | null) {
        this.control()?.setValue(value);
    }
    public get current$(): Observable<IUploadedFile[] | File[] | null> {
        return this.control()?.valueChanges as Observable<IUploadedFile[] | File[] | null>;
    }

    protected _currentExistance: { exists: boolean, file_details: IUploadedFile; }[] | null = null;
    get currentUploadeds(): { exists: boolean, file_details: IUploadedFile; }[] | null {
        if (this.mode !== FileUploaderMode.DOWNLOADS) return null;
        return this._currentExistance;
        // return this.current as IUploadedFile[];
    }

    get currentFiles(): File[] | null {
        if (this.mode !== FileUploaderMode.UPLOAD) return null;
        return this.current as File[];
    }

    get none_exists(): boolean | undefined {
        return this._currentExistance?.every(c => !c.exists);
    }

    get acceptedFormats() {
        if (this.hasFormatWildcard) {
            return '*/*';
        }

        const toReturn: string[] = [];

        for (const oneExt of this.fileFormats()) {
            if (this.fileTypes[oneExt]) {
                if (typeof this.fileTypes[oneExt] === "string") {
                    toReturn.push(this.fileTypes[oneExt] as string);
                } else if (_.isArray(this.fileTypes[oneExt])) {
                    toReturn.push(...this.fileTypes[oneExt]);
                }
            }

            toReturn.push("." + oneExt);
        }

        return _.uniq(toReturn).join(",");
    }
    private thumbnail_img: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);
    get thumbnail_img$(): Observable<string | null> {
        return this.thumbnail_img.asObservable();
    }

    private subs: Subscription = new Subscription();

    async ngOnInit() {
        this.subs.add(this.current$.subscribe(this.onValueChange.bind(this)));

        const v = this.control()?.value;
        await this.onValueChange(v);

        // this.uploaderId = ;
        this.subs.add(
            this.fileControl.valueChanges.subscribe(value => {
                if (!this.control()) {
                    return;
                }
                if (this.mode === FileUploaderMode.UPLOAD) {
                    const files: File[] | undefined = (document.querySelector("#" + this.uploaderId) as any)?.files;
                    if (files) {
                        const testedFiles: { file: File, valid: boolean; }[] = this.testFiles(files);
                        const filteredFiles = testedFiles.filter((e: any) => e.valid).map((e: any) => e.file);
                        if (filteredFiles && filteredFiles.length > 0) {
                            this.current = filteredFiles;
                        }
                    } else {
                        this.current = null;
                    }
                }
                this.cd.markForCheck();
            })
        );
    }

    async onValueChange(v: any) {
        this.selectMode(v);
        if (this.current && this.mode === FileUploaderMode.DOWNLOADS) {
            this._currentExistance = await this.checkExistance();
            // console.log('[FILE UPLOADER] current existances', this._currentExistance);

            // check for thumbnails 
            const thumb = this._currentExistance?.find(c => !!c.file_details.thumbnail);
            if (thumb?.file_details.thumbnail) {
                this.thumbnail_img.next(await this.s_files.getFileThumbnailImg(thumb.file_details.thumbnail.filename));
            } else {
                this.thumbnail_img.next(null);
            }
            // console.log('[FILE UPLOADER] thumbnail', this.thumbnail_img);
        }
        this.cd.markForCheck();
    }

    selectMode(v: File | File[] | IUploadedFile | IUploadedFile[] | null) {
        if (v instanceof File || (_.isArray(v) && _.every(v, (_v: IUploadedFile | File) => _v instanceof File))) {
            this.mode = FileUploaderMode.UPLOAD;
        } else if (!!v && ((_.isArray(v) && !_.isEmpty(v)) || !_.isArray(v))) {
            this.mode = FileUploaderMode.DOWNLOADS;
        } else if (!v) {
            this.mode = FileUploaderMode.UPLOAD;
        }
    }

    ngOnDestroy(): void {
        this.subs.unsubscribe();
    }

    openFolder() {
        if (this.isClient) {
            return;
        }

        if (this.currentUploadeds) {
            const folder = this.currentUploadeds[0].file_details?.cf_path.replace(this.currentUploadeds[0].file_details.filename, '');
            this.s_project.openFolder(folder);
        }
    }

    private generateUniqueIdentifier(length: number): string {
        const chars: string = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";

        const ts: number = Math.floor(Date.now() / 1000);
        let uniqueString: string = "";

        if (length > 0) {
            for (let i = 0; i < length; i++) {
                uniqueString += chars[Math.floor(Math.random() * chars.length)];
            }
        }

        return `${ts.toString()}${uniqueString.length > 0 ? uniqueString : ""}`;
    }

    public isArray(object: any): boolean {
        return _.isArray(object);
    }

    public async checkExistance() {
        if (this.current && this.mode !== FileUploaderMode.DOWNLOADS) {
            return null;
        }
        return await this.s_files.existsFiles(this.current as IUploadedFile[] || []);
    }

    //#region FILE
    public async download() {

        if (this.none_exists) {
            return;
        }
        if (this.mode !== FileUploaderMode.DOWNLOADS) {
            return;
        }

        if (_.isArray(this.current)) {
            await this.s_files.downloadFiles(this.currentUploadeds!.filter(c => c.exists).map(c => c.file_details));
        }
        // else if (this.currentUploadedFiles) {
        //     await this.s_files.downloadFile(this.currentUploadedFiles);
        // }
    }

    public openFileUploaderInput(): void {
        if (this.readOnly) {
            return;
        }
        if (document && document.querySelector("#" + this.uploaderId) && !this.current) {
            (document.querySelector("#" + this.uploaderId)! as HTMLElement).click();
        }
    }

    public clearUploader(event: MouseEvent): void {
        event.preventDefault();
        event.stopPropagation();

        if (this.mode === FileUploaderMode.DOWNLOADS) {
            this.mode = FileUploaderMode.UPLOAD;

            this.fileControl!.setValue(null);
            this.current = null;
        } else {
            if (document && document.querySelector("#" + this.uploaderId)) {
                (document.querySelector("#" + this.uploaderId)! as any).value = null;
            }
            this.fileControl!.setValue(null);
            this.current = null;
        }
        this.setArchivingStatus(false);
        // this.selectMode(null);
    }

    public clearAndUpload(event: MouseEvent): void {
        this.clearUploader(event);
        setTimeout(() => {
            this.openFileUploaderInput();
        }, 5);

    }

    public async droppedFile(event: DragEvent) {
        event.preventDefault();
        if (this.readOnly) {
            return;
        }

        const rawfiles: File[] = [];
        const traverseFileTree = async (item: any, path?: string) => {
            if (item.isFile) {
                // Get file
                await new Promise<void>((resolve, reject) => {
                    item.file(function (file: File) {
                        rawfiles.push(file);
                        // console.log("File:", path + file.name);
                        resolve();
                    });
                });
            } else if (item.isDirectory) {

                this.setArchivingStatus(true);
                // Zip the directory and add the zipped file to rawfiles
                const zipBlob = await this.zipDirectory(item);
                if (this.archiving) {
                    rawfiles.push(new File([zipBlob], `${item.name}.zip`));
                    this.setArchivingStatus(false);
                }
            }
        };

        const items: DataTransferItemList | undefined = event.dataTransfer?.items;
        if (!items) { return; }
        for (const dataTransferItem of Array.from(items)) {
            // webkitGetAsEntry is where the magic happens
            const item = dataTransferItem.webkitGetAsEntry();
            if (item) {
                await traverseFileTree(item);
            }
        }

        let files = [];
        if (this.isMultipleANumber) {
            files = rawfiles.slice(0, this.multiple as number);
        } else if (this.multiple) {
            files = rawfiles;
        } else {
            files = rawfiles.slice(0, 1);
        }
        const testedFiles: { file: File, valid: boolean; }[] = this.testFiles(rawfiles);
        const validFiles: File[] = testedFiles.filter((e: any) => e.valid).map((e: any) => e.file);
        this.current = validFiles.length ? validFiles : null;
    }

    public setArchivingStatus(isArchiving: boolean) {
        const control = this.control()!;
        if (isArchiving) {
            this.archiving = true;
            const errors = this.control()!.errors || {};
            errors['archiving'] = true;
            this.control()!.setErrors(errors);
            // const status = this.s_files.handleProgression('archiving');
        } else {
            this.archiving = false;
            // status.uploading.next(false);
            if (control.errors) {
                const errors = { ...control.errors };
                delete errors['archiving'];
                control.setErrors(Object.keys(errors).length ? errors : null);
            }
        }
        control.parent?.updateValueAndValidity();
    }

    private async zipDirectory(item: any): Promise<Blob> {
        const zip = new JSZip();
        await this.addDirectoryToZip(item, zip);
        return await zip.generateAsync({ type: "blob" });
    }

    private async addDirectoryToZip(item: any, zipFolder: any) {
        const dirReader = item.createReader();
        const entries: any[] = await new Promise((resolve, reject) => {
            dirReader.readEntries(resolve, reject);
        });
        for (const entry of entries) {
            if (entry.isFile && entry.name !== ".DS_Store") {
                await new Promise<void>((resolve, reject) => {
                    entry.file(function (file: File) {
                        const reader = new FileReader();
                        reader.onload = function (e) {
                            zipFolder.file(file.name, e.target!.result);
                            resolve();
                        };
                        reader.onerror = error => reject(error);
                        reader.readAsArrayBuffer(file);
                    });
                });
            } else if (entry.isDirectory) {
                const folder = zipFolder.folder(entry.name);
                await this.addDirectoryToZip(entry, folder);
            }
        }
    }

    public preventDragOver(event: any): void {
        event.preventDefault();
    }

    public setHover(add: boolean): void {
        if (this.readOnly) {
            return;
        }
        if (add) {
            this.upBlock()!.nativeElement.classList.add("hovered");
        } else {
            this.upBlock()!.nativeElement.classList.remove("hovered");
        }
    }

    private async fileToBase64(file: File): Promise<string | ArrayBuffer | null> {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => resolve(reader.result);
            reader.onerror = error => reject(error);
        });
    }

    private testFile(file: File | null | undefined, fileFormats: string[]) {
        let fileIsOk: boolean = true;

        if (!file || !(file instanceof File) || !this.isFormatValid(file, fileFormats) || file.size > this.maxFileSize() * 1000 * 1000) {
            fileIsOk = false;

            if (file && !this.isFormatValid(file, fileFormats)) {
                this.alert.error("INVALID_FILE_TYPE");
            }

            if (file && file.size > this.maxFileSize() * 1000 * 1000) {
                this.alert.error("INVALID_FILE_SIZE");
            }
        }

        return fileIsOk;
    }

    private testFiles(files: File[]) {
        let toTestFiles: File | File[] | null;
        let testedFiles: { file: File, valid: boolean; }[] = [];

        if (this.multiple) {
            if (_.isNumber(this.multiple)) {
                toTestFiles = [...files].slice(0, this.multiple);
            } else {
                toTestFiles = files;
            }
        } else {
            toTestFiles = files[0];
        }

        if (toTestFiles) {
            testedFiles = [];
            if (!_.isArray(toTestFiles)) {
                testedFiles.push({
                    file: toTestFiles,
                    valid: this.testFile(toTestFiles, this.fileFormats())
                });
            } else {
                for (const oneFile of toTestFiles) {
                    testedFiles.push({
                        file: oneFile,
                        valid: this.testFile(oneFile, this.fileFormats())
                    });
                }
            }
        }

        return testedFiles;
    }

    private isFormatValid(file: File, fileFormats: string[]) {
        if (this.hasFormatWildcard) {
            return true;
        }

        let isExtensionValid: boolean = false;
        let isTypeValid: boolean = false;

        // TMP DISABLE FOR DEV 
        for (const oneFormat of fileFormats) {
            if (file.name.endsWith(`.${oneFormat}`)) {
                isExtensionValid = true;
            }

        }


        for (const oneFormat of fileFormats) {
                                console.log('file.type', file.type)

            if (_.isString(this.fileTypes[oneFormat])) {
                if (file.type === this.fileTypes[oneFormat]) {
                    isTypeValid = true;
                }
            } else if (_.isArray(this.fileTypes[oneFormat])) {
                if (this.fileTypes[oneFormat].includes(file.type)) {
                    isTypeValid = true;
                }
            }
        }

        return isExtensionValid && isTypeValid;
    }
    //#endregion FILE

    getSize(size: number): string {
        const s = size / 1000;
        if (s > 100) {
            return (size / 1000000).toFixed(1) + 'Mo';
        } else {
            return (size / 1000).toFixed(1) + 'Ko';

        }
    }

    isCPP() {
        this.s_auth.isCPP();
    }
}
